import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from '../../../../shared/i18n';

/**
 * InstructionsModal - Generic modal for showing instruction images with carousel
 *
 * Supports two modes:
 * - "view" (default): Standard carousel with wrap-around navigation and Close button
 * - "onboarding": Linear navigation with Next/Start buttons, calls onComplete when done
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Function} props.onHide - Callback to close the modal
 * @param {Array<{src: string, alt: string}>} props.images - Array of image objects
 * @param {string} props.title - Modal title
 * @param {string} props.testId - data-testid for the modal
 * @param {string} props.mode - "view" (default) or "onboarding"
 * @param {Function} props.onComplete - Callback when onboarding is complete (only used in onboarding mode)
 */
export const InstructionsModal = ({
  show,
  onHide,
  images = [],
  title,
  testId,
  mode = 'view',
  onComplete
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  const isOnboarding = mode === 'onboarding';
  const isLastSlide = currentIndex === images.length - 1;
  const isFirstSlide = currentIndex === 0;

  // Handle case where there are no images - skip to onComplete in onboarding mode
  useEffect(() => {
    if (show && images.length === 0 && isOnboarding && onComplete) {
      onComplete();
    }
  }, [show, images.length, isOnboarding, onComplete]);

  const handlePrevious = useCallback(() => {
    if (isOnboarding) {
      // No wrap-around in onboarding mode
      setCurrentIndex((prev) => Math.max(0, prev - 1));
    } else {
      setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    }
  }, [images.length, isOnboarding]);

  const handleNext = useCallback(() => {
    if (isOnboarding) {
      // No wrap-around in onboarding mode
      setCurrentIndex((prev) => Math.min(images.length - 1, prev + 1));
    } else {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }
  }, [images.length, isOnboarding]);

  const handleClose = useCallback(() => {
    setCurrentIndex(0); // Reset to first image when closing
    onHide();
    // In onboarding mode, closing early should also continue the flow to credentials
    if (isOnboarding && onComplete) {
      onComplete();
    }
  }, [onHide, isOnboarding, onComplete]);

  const handleStart = useCallback(() => {
    setCurrentIndex(0); // Reset for next time
    onHide();
    if (onComplete) {
      onComplete();
    }
  }, [onHide, onComplete]);

  if (!show || images.length === 0) return null;

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
      data-testid={testId}
      onClick={handleClose}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content bg-dark text-white">
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title fw-bold">
              {title}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
              aria-label={t('common.close')}
            ></button>
          </div>
          <div className="modal-body p-3">
            {/* Image container */}
            <div className="position-relative" style={{ minHeight: '400px' }}>
              <img
                src={images[currentIndex].src}
                alt={images[currentIndex].alt}
                className="w-100 rounded"
                style={{ maxHeight: '70vh', objectFit: 'contain' }}
              />

              {/* Navigation arrows */}
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="btn btn-dark btn-lg position-absolute top-50 start-0 translate-middle-y ms-2 rounded-circle"
                    style={{ opacity: isOnboarding && isFirstSlide ? 0.3 : 0.8, width: '50px', height: '50px' }}
                    onClick={handlePrevious}
                    disabled={isOnboarding && isFirstSlide}
                    aria-label={t('common.previous')}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-dark btn-lg position-absolute top-50 end-0 translate-middle-y me-2 rounded-circle"
                    style={{ opacity: isOnboarding && isLastSlide ? 0.3 : 0.8, width: '50px', height: '50px' }}
                    onClick={handleNext}
                    disabled={isOnboarding && isLastSlide}
                    aria-label={t('common.next')}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </>
              )}
            </div>

            {/* Pagination indicators */}
            <div className="d-flex justify-content-center gap-2 mt-3" data-testid="pagination-dots">
              {images.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className="rounded-circle pagination-dot"
                  style={{
                    width: '12px',
                    height: '12px',
                    minWidth: '12px',
                    padding: 0,
                    border: '2px solid white',
                    backgroundColor: index === currentIndex ? 'white' : 'transparent'
                  }}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`${t('children.activity_sessions.instructions_modal.step')} ${index + 1}`}
                />
              ))}
            </div>

            {/* Step counter */}
            <p className="text-center text-muted mt-2 mb-0">
              {currentIndex + 1} / {images.length}
            </p>
          </div>
          <div className="modal-footer border-0 pt-0">
            {isOnboarding ? (
              isLastSlide ? (
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleStart}
                  data-testid="onboarding-start-button"
                >
                  {t('children.activity_sessions.onboarding.start')}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleNext}
                  data-testid="onboarding-next-button"
                >
                  {t('common.next')}
                </button>
              )
            ) : (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
              >
                {t('common.close')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
